import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

type ToastIntent = 'info' | 'success' | 'warning' | 'error' | 'progress' | 'avatar';
type ToastPosition = 'top-end' | 'top-start' | 'bottom-end' | 'bottom-start' | 'top' | 'bottom';

type IUseToastParams = {
  toasterId?: string;
  inline?: boolean;
  offset?: {
    x?: number;
    y?: number;
  };
};

type IToastParams = {
  intent?: ToastIntent;
  msg: ReactNode;
  timeout?: number;
  avatarName?: string;
  position?: ToastPosition;
  isUpdate?: boolean;
};

type ActiveToast = {
  id: number;
  intent: ToastIntent;
  msg: ReactNode;
  avatarName?: string;
  position: ToastPosition;
};

const POSITION_STYLE: Record<ToastPosition, React.CSSProperties> = {
  'top-end': { top: 16, right: 16, alignItems: 'flex-end' },
  'top-start': { top: 16, left: 16, alignItems: 'flex-start' },
  'bottom-end': { bottom: 16, right: 16, alignItems: 'flex-end' },
  'bottom-start': { bottom: 16, left: 16, alignItems: 'flex-start' },
  top: { top: 16, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' },
  bottom: { bottom: 16, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' }
};

const INTENT_BORDER: Record<string, string> = {
  error: '#d13438',
  warning: '#fce100',
  success: '#107c10',
  info: '#0078d4',
  progress: '#0078d4',
  avatar: '#0078d4'
};

/**
 * Lightweight toaster that keeps the previous useToast() API without
 * depending on @fluentui/react-components.
 */
const useToast = (params?: IUseToastParams) => {
  const { inline, offset } = params ?? {};
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextIdRef = useRef(1);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const clearTimer = (id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  };

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, []);

  const dismiss = useCallback(() => {
    setToasts((prev) => {
      for (const t of prev) clearTimer(t.id);
      return [];
    });
  }, []);

  const toast = useCallback((toastParams: IToastParams) => {
    const { intent = 'info', msg, avatarName, position = 'top-end', timeout = 4000, isUpdate = false } = toastParams;

    setToasts((prev) => {
      const entry: ActiveToast = {
        id: isUpdate && prev.length > 0 ? prev[prev.length - 1].id : nextIdRef.current++,
        intent,
        msg,
        avatarName,
        position
      };

      if (isUpdate && prev.length > 0) {
        clearTimer(entry.id);
        const next = [...prev.slice(0, -1), entry];
        if (timeout && timeout > 0) {
          timersRef.current.set(
            entry.id,
            setTimeout(() => {
              setToasts((cur) => cur.filter((t) => t.id !== entry.id));
              timersRef.current.delete(entry.id);
            }, timeout)
          );
        }
        return next;
      }

      if (timeout && timeout > 0) {
        timersRef.current.set(
          entry.id,
          setTimeout(() => {
            setToasts((cur) => cur.filter((t) => t.id !== entry.id));
            timersRef.current.delete(entry.id);
          }, timeout)
        );
      }
      return [...prev, entry];
    });
  }, []);

  const render = () => {
    if (toasts.length === 0) {
      return null;
    }

    // Group by position so multiple toasts stack correctly.
    const byPosition = new Map<ToastPosition, ActiveToast[]>();
    for (const t of toasts) {
      const list = byPosition.get(t.position) ?? [];
      list.push(t);
      byPosition.set(t.position, list);
    }

    const { x = 0, y = 0 } = offset ?? {};

    return (
      <>
        {[...byPosition.entries()].map(([position, list]) => (
          <div
            key={position}
            className='idigest-chat-toaster'
            style={{
              position: inline ? 'absolute' : 'fixed',
              zIndex: 100000,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              pointerEvents: 'none',
              ...POSITION_STYLE[position],
              ...(x || y
                ? {
                    marginRight: position.includes('end') ? x : undefined,
                    marginLeft: position.includes('start') ? x : undefined,
                    marginTop: position.startsWith('top') ? y : undefined,
                    marginBottom: position.startsWith('bottom') ? y : undefined
                  }
                : null)
            }}
          >
            {list.map((t) => (
              <div
                key={t.id}
                role='status'
                style={{
                  pointerEvents: 'auto',
                  minWidth: 200,
                  maxWidth: 360,
                  padding: '10px 14px',
                  borderRadius: 8,
                  background: 'var(--idigest-chat-dialog-bg, #fff)',
                  color: 'var(--idigest-chat-dialog-fg, #323130)',
                  borderLeft: `4px solid ${INTENT_BORDER[t.intent] ?? INTENT_BORDER.info}`,
                  boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                  fontSize: 14,
                  lineHeight: 1.4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                {t.intent === 'progress' ? (
                  <span
                    aria-hidden
                    style={{
                      width: 14,
                      height: 14,
                      border: '2px solid #c8c6c4',
                      borderTopColor: '#0078d4',
                      borderRadius: '50%',
                      display: 'inline-block',
                      animation: 'idigest-chat-toast-spin 0.8s linear infinite',
                      flexShrink: 0
                    }}
                  />
                ) : null}
                {t.intent === 'avatar' && t.avatarName ? (
                  <span
                    aria-hidden
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: '#0078d4',
                      color: '#fff',
                      fontSize: 10,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    {t.avatarName.slice(0, 1).toUpperCase()}
                  </span>
                ) : null}
                <div style={{ flex: 1, minWidth: 0 }}>{t.msg}</div>
              </div>
            ))}
          </div>
        ))}
        <style>{`@keyframes idigest-chat-toast-spin{to{transform:rotate(360deg)}}`}</style>
      </>
    );
  };

  return {
    toast,
    dismiss,
    render
  };
};

export default useToast;
